/**
 * WebSocket protocol between client and room server.
 *
 * Every frame is a JSON object with a `type` field. The authoritative description with
 * sequencing rules lives in docs/protocol.md; keep the two in sync. Phase 0 defines membership,
 * room listing and query notifications. Pose, voice signalling, whiteboard and presenter frames
 * are added in later phases; Phase 6 adds the whiteboard, shelf models, presenter, screen
 * share and reactions (room settings were retired on 2026-10-07). Since 2026-10-06 study rooms are entered with a three
 * character code (`rooms.ts`), everything a room shows is shared through the room's view
 * state (`view:set`), and the host's lines are spoken to the whole room (`host:say`).
 */
import type { Query } from './query.ts';
import type { DashboardId, Role } from './query.ts';

export const LOBBY_ROOM = 'lobby';
export const ROOM_CAPACITY = 12;
export const POSE_RATE_HZ = 20;
export const POSE_INTERPOLATION_MS = 100;
export const FULL_BODY_AVATAR_LIMIT = 12;
export const NAME_TAG_FADE_START_M = 8;
export const TABLE_PIN_SLOTS = 8;
export const LOBBY_SOFT_CAP = 24;

export interface MemberInfo {
  sessionId: string;
  name: string;
  role: Role;
  avatar: string;
  joinedAt: string;
  muted: boolean;
  /** Calibrated standing eye height in metres, when known. */
  height?: number;
  /** Hand raised (flat palm held up, or the wrist-menu / H key). */
  handRaised?: boolean;
}

export interface RoomSummary {
  code: string;
  title: string;
  phase: string;
  therapeuticArea: string;
  siteCount: number;
  enrolled: number;
  target: number;
  openQueries: number;
  capacity: number;
  /**
   * How many people are in the study's rooms. Never who: this goes to everyone, and people
   * meet only by sharing a room code (2026-10-07).
   */
  people: number;
  /** How many rooms (codes) of the study have people in them; the codes are not listed. */
  rooms: number;
}

/* ---------- Client to server ---------- */

export interface HelloMessage {
  type: 'hello';
  name: string;
  role: Role;
  avatar: string;
}

export interface JoinMessage {
  type: 'join';
  /** 'lobby' or a study room key `<study>.<code>` such as 'ONC-2041.K7P'. */
  room: string;
}

export interface LeaveMessage {
  type: 'leave';
}

export interface PingMessage {
  type: 'ping';
  t: number;
}

/* ---------- Pose sync (20 Hz) ---------- */

/** One tracked hand or controller. Positions are world metres, quaternions xyzw. */
export interface PoseHand {
  p: [number, number, number];
  q: [number, number, number, number];
  kind: 'controller' | 'hand';
  /** 0..1 pinch strength (hand tracking) or trigger value (controller). */
  pinch: number;
  /** Per-finger curl 0..1: thumb, index, middle, ring, pinky. */
  curl: [number, number, number, number, number];
}

export interface PoseData {
  /** Sender's clock, ms. */
  t: number;
  /** Head position and orientation: x y z qx qy qz qw. */
  head: [number, number, number, number, number, number, number];
  hands: [PoseHand | null, PoseHand | null];
  /** Locomotion state: 0 idle, 1 smooth-moving, 2 teleported this frame. */
  moving: 0 | 1 | 2;
  speaking: boolean;
  /** Calibrated standing eye height in metres. */
  height: number;
}

export interface PoseMessage extends PoseData {
  type: 'pose';
}

/** WebRTC signalling relayed through the server (offer/answer/ICE). */
export interface RtcSignalMessage {
  type: 'rtc:signal';
  to: string;
  data: unknown;
}

export interface MemberUpdateMessage {
  type: 'member:update';
  muted?: boolean;
  height?: number;
  handRaised?: boolean;
}

/* ---------- Dashboards (Phase 3) ---------- */

/** Dashboard filters: the room's, and each screen's (shared with the room since 2026-10-06). */
export interface DashboardFilters {
  siteId?: string;
  arm?: string;
  /** Inclusive visit code range, in schedule order. */
  visitFrom?: string;
  visitTo?: string;
  /** Inclusive ISO date range applied to dated records. */
  dateFrom?: string;
  dateTo?: string;
  /** One subject: every dashboard shows only them. */
  subjectId?: string;
  /** The subjects summoned out of the ward for review: every dashboard shows only them. */
  subjectIds?: string[];
  /** Patient filters from the ward console: sex, status, a flag, an age band, a BMI bucket. */
  sex?: 'F' | 'M';
  status?: string;
  flag?: string;
  ageBand?: string;
  bmiBucket?: string;
}

/** A dashboard clone pinned to the collaboration table, shared with the room. */
export interface PinnedPanel {
  id: string;
  dashboard: DashboardId;
  /** Serialised view state (filters + drill path) so everyone sees the same clone. */
  state: { filters: DashboardFilters; path: string[] };
  /** Slot index around the table, 0..7. */
  slot: number;
  pinnedBy: { sessionId: string; name: string };
  pinnedAt: string;
}

export interface PinAddMessage {
  type: 'pin:add';
  dashboard: DashboardId;
  state: PinnedPanel['state'];
}

export interface PinRemoveMessage {
  type: 'pin:remove';
  id: string;
}

/** "Broadcast": draw everyone's attention to a dashboard without moving them. */
export interface BroadcastViewMessage {
  type: 'broadcast:view';
  dashboard: DashboardId;
  path: string[];
}

/** A shared note dropped on something in the room; carried in the late-join snapshot. */
export interface Annotation {
  id: string;
  position: { x: number; y: number; z: number };
  text: string;
  dashboard?: DashboardId;
  author: { sessionId: string; name: string; role: Role };
  at: string;
}

export interface AnnotationAddMessage {
  type: 'annotation:add';
  position: { x: number; y: number; z: number };
  text: string;
  dashboard?: DashboardId;
}

export interface AnnotationRemoveMessage {
  type: 'annotation:remove';
  id: string;
}

/** Presenter beam: where the sender points, at most 10 Hz; target null ends it. */
export interface BeamMessage {
  type: 'beam';
  origin: [number, number, number];
  target: [number, number, number] | null;
}

export const ANNOTATION_LIMIT = 200;

/* ---------- Shared room view (2026-10-06) ---------- */

/**
 * Everything a study room shows is the same for everyone in it: the room filter, each
 * screen's filters, drill path and placement, the centre screen, the patients out for review,
 * the ward's overlays and the host. Each piece is a keyed JSON value; the server keeps the
 * latest per key (with a sequence number, so a late joiner replays them in order) and relays
 * changes to everyone else. A null value removes the key. Cleared when the room empties.
 */
export interface ViewEntry {
  key: string;
  value: unknown;
  /** Assigned by the server; later changes have higher numbers. */
  seq: number;
  /** Session that set it. */
  by: string;
}

export const VIEW_LIMITS = { keys: 400, keyLength: 80, valueBytes: 16_000, perMessage: 64 };

export interface ViewSetMessage {
  type: 'view:set';
  entries: { key: string; value: unknown }[];
}

/* ---------- The host (2026-10-06): one Luma per room ---------- */

export const HOST_SAY_KINDS = ['welcome', 'tour', 'answer', 'summary', 'other'] as const;
export type HostSayKind = (typeof HOST_SAY_KINDS)[number];

/** A line for the host to say: everyone in the room hears it, in the order the server got it. */
export interface HostSayMessage {
  type: 'host:say';
  /** Client-made id, so the sender knows when its own line comes back. */
  id: string;
  text: string;
  kind: HostSayKind;
  /** Who she is talking to, if anyone. */
  to?: string;
  /** What she was asked (answers). */
  question?: string;
}

/** Cuts the host off (Stop, End tour): every client stops her voice. */
export interface HostStopMessage {
  type: 'host:stop';
}

/* ---------- Collaboration tools (Phase 6) ---------- */

/** The whiteboard wall: 6 x 2 m; strokes and notes are in board units 0..1 (u right, v down). */
export const WHITEBOARD = {
  width: 6,
  height: 2,
  strokeLimit: 1500,
  pointLimit: 600,
  noteLimit: 60,
  colours: ['#17212f', '#1f8a8a', '#b0233a', '#d9931f', '#2e6b3a', '#6f4bb3'],
  /** Pen widths in board units (6 m wide: 0.0015 is 9 mm). */
  widths: [0.0015, 0.003, 0.006],
  noteColours: ['#ffe680', '#b6f0c2', '#bfe3ff', '#ffc2d4'],
} as const;

export interface WbStroke {
  id: string;
  by: { sessionId: string; name: string };
  colour: string;
  width: number;
  /** Flat u,v pairs. */
  points: number[];
  /** False while the pen is still down; the sender keeps replacing the stroke by id. */
  done: boolean;
}

export interface WbNote {
  id: string;
  by: { sessionId: string; name: string };
  u: number;
  v: number;
  text: string;
  colour: string;
}

export interface WhiteboardState {
  strokes: WbStroke[];
  notes: WbNote[];
}

export interface WbStrokeMessage {
  type: 'wb:stroke';
  id: string;
  colour: string;
  width: number;
  points: number[];
  done: boolean;
}

export interface WbNoteMessage {
  type: 'wb:note';
  /** Omitted to add a note; an existing id moves or edits it. */
  id?: string;
  u: number;
  v: number;
  text: string;
  colour: string;
}

export interface WbEraseMessage {
  type: 'wb:erase';
  ids: string[];
}

/** Removes the sender's most recent stroke or note. */
export interface WbUndoMessage {
  type: 'wb:undo';
}

export interface WbClearMessage {
  type: 'wb:clear';
}

/** The shelf models that can stand on the table. */
export const MODEL_KINDS = ['torso', 'molecule', 'siteMap', 'dosing'] as const;
export type ModelKind = (typeof MODEL_KINDS)[number];
export const MODEL_LIMIT = 6;

export interface ModelPin {
  id: string;
  /** Part name inside the model. */
  part: string;
  by: { sessionId: string; name: string; role: Role };
  at: string;
}

export interface SharedModel {
  id: string;
  kind: ModelKind;
  position: { x: number; y: number; z: number };
  quaternion: [number, number, number, number];
  scale: number;
  /** 0 assembled .. 1 fully exploded. */
  exploded: number;
  /** Whoever is holding it; others see a ghost outline. */
  owner: { sessionId: string; name: string } | null;
  pins: ModelPin[];
  placedBy: { sessionId: string; name: string };
}

export interface ModelPlaceMessage {
  type: 'model:place';
  kind: ModelKind;
  position: { x: number; y: number; z: number };
  quaternion: [number, number, number, number];
  scale: number;
}

export interface ModelUpdateMessage {
  type: 'model:update';
  id: string;
  position?: { x: number; y: number; z: number };
  quaternion?: [number, number, number, number];
  scale?: number;
  exploded?: number;
}

export interface ModelGrabMessage {
  type: 'model:grab';
  id: string;
  on: boolean;
}

export interface ModelPinMessage {
  type: 'model:pin';
  id: string;
  part: string;
}

export interface ModelUnpinMessage {
  type: 'model:unpin';
  id: string;
  pinId: string;
}

export interface ModelRemoveMessage {
  type: 'model:remove';
  id: string;
}

/** A presentation slide: a dashboard view or a pinned clone. */
export type Slide =
  | { kind: 'dashboard'; dashboard: DashboardId; path: string[]; filters: DashboardFilters }
  | { kind: 'pin'; id: string };

export interface PresenterState {
  sessionId: string;
  name: string;
  slide: Slide | null;
  spotlight: boolean;
  startedAt: string;
}

export interface PresentStartMessage {
  type: 'present:start';
}

export interface PresentEndMessage {
  type: 'present:end';
}

export interface PresentSlideMessage {
  type: 'present:slide';
  slide: Slide | null;
}

export interface PresentSpotlightMessage {
  type: 'present:spotlight';
  on: boolean;
}

/** "Pull everyone to me": a consent prompt on every other client. */
export interface PresentRecentreMessage {
  type: 'present:recentre';
}

export interface ShareStateMessage {
  type: 'share:state';
  on: boolean;
}

export const REACTIONS = ['thumbsUp', 'clap', 'wave', 'question'] as const;
export type Reaction = (typeof REACTIONS)[number];

export interface ReactionMessage {
  type: 'reaction';
  reaction: Reaction;
}

export type ClientMessage =
  | HelloMessage
  | JoinMessage
  | LeaveMessage
  | PingMessage
  | PoseMessage
  | RtcSignalMessage
  | MemberUpdateMessage
  | PinAddMessage
  | PinRemoveMessage
  | BroadcastViewMessage
  | ViewSetMessage
  | HostSayMessage
  | HostStopMessage
  | AnnotationAddMessage
  | AnnotationRemoveMessage
  | BeamMessage
  | WbStrokeMessage
  | WbNoteMessage
  | WbEraseMessage
  | WbUndoMessage
  | WbClearMessage
  | ModelPlaceMessage
  | ModelUpdateMessage
  | ModelGrabMessage
  | ModelPinMessage
  | ModelUnpinMessage
  | ModelRemoveMessage
  | PresentStartMessage
  | PresentEndMessage
  | PresentSlideMessage
  | PresentSpotlightMessage
  | PresentRecentreMessage
  | ShareStateMessage
  | ReactionMessage;

/* ---------- Server to client ---------- */

export interface WelcomeMessage {
  type: 'welcome';
  sessionId: string;
  serverTime: string;
}

export interface RoomSnapshotMessage {
  type: 'room:snapshot';
  room: string;
  members: MemberInfo[];
  /** Last known pose per member, so late joiners see everyone in place immediately. */
  poses: Record<string, PoseData>;
  /** Dashboard clones currently on the table. */
  pins: PinnedPanel[];
  /** Shared notes in the room. */
  annotations: Annotation[];
  /** Collaboration tools (Phase 6); absent for the lobby. */
  whiteboard?: WhiteboardState;
  models?: SharedModel[];
  presenter?: PresenterState | null;
  /** Who is sharing a screen to the presentation wall. */
  sharer?: { sessionId: string; name: string } | null;
  /** The room's shared view, oldest change first. */
  view?: ViewEntry[];
}

export interface ViewSetRelayMessage {
  type: 'view:set';
  room: string;
  from: { sessionId: string; name: string };
  entries: ViewEntry[];
}

export interface HostSayRelayMessage {
  type: 'host:say';
  room: string;
  from: { sessionId: string; name: string };
  id: string;
  text: string;
  kind: HostSayKind;
  to?: string;
  question?: string;
}

export interface HostStopRelayMessage {
  type: 'host:stop';
  room: string;
  from: { sessionId: string; name: string };
}

export interface WbStrokeRelayMessage {
  type: 'wb:stroke';
  room: string;
  stroke: WbStroke;
}

export interface WbNoteRelayMessage {
  type: 'wb:note';
  room: string;
  note: WbNote;
}

export interface WbEraseRelayMessage {
  type: 'wb:erase';
  room: string;
  ids: string[];
}

export interface WbClearRelayMessage {
  type: 'wb:clear';
  room: string;
  by: { sessionId: string; name: string };
}

export interface ModelPlacedMessage {
  type: 'model:placed';
  room: string;
  model: SharedModel;
}

export interface ModelUpdatedMessage {
  type: 'model:updated';
  room: string;
  id: string;
  from: string;
  patch: Partial<Pick<SharedModel, 'position' | 'quaternion' | 'scale' | 'exploded' | 'owner'>>;
}

export interface ModelPinnedMessage {
  type: 'model:pinned';
  room: string;
  id: string;
  pin: ModelPin;
}

export interface ModelUnpinnedMessage {
  type: 'model:unpinned';
  room: string;
  id: string;
  pinId: string;
}

export interface ModelRemovedMessage {
  type: 'model:removed';
  room: string;
  id: string;
}

export interface PresentStateMessage {
  type: 'present:state';
  room: string;
  presenter: PresenterState | null;
}

export interface PresentRecentreRelayMessage {
  type: 'present:recentre';
  room: string;
  from: { sessionId: string; name: string };
}

export interface ShareStateRelayMessage {
  type: 'share:state';
  room: string;
  sessionId: string;
  name: string;
  on: boolean;
}

export interface ReactionRelayMessage {
  type: 'reaction';
  room: string;
  from: { sessionId: string; name: string };
  reaction: Reaction;
}

export interface AnnotationAddedMessage {
  type: 'annotation:added';
  room: string;
  annotation: Annotation;
}

export interface AnnotationRemovedMessage {
  type: 'annotation:removed';
  room: string;
  id: string;
}

export interface BeamRelayMessage {
  type: 'beam';
  room: string;
  from: { sessionId: string; name: string };
  origin: [number, number, number];
  target: [number, number, number] | null;
}

export interface PinAddedMessage {
  type: 'pin:added';
  room: string;
  pin: PinnedPanel;
}

export interface PinRemovedMessage {
  type: 'pin:removed';
  room: string;
  id: string;
}

export interface ViewBroadcastMessage {
  type: 'broadcast:view';
  room: string;
  from: { sessionId: string; name: string };
  dashboard: DashboardId;
  path: string[];
}

export interface RemotePoseMessage extends PoseData {
  type: 'pose';
  id: string;
}

export interface RtcSignalRelayMessage {
  type: 'rtc:signal';
  from: string;
  data: unknown;
}

export interface MemberUpdatedMessage {
  type: 'member:update';
  room: string;
  sessionId: string;
  muted?: boolean;
  height?: number;
  handRaised?: boolean;
}

export interface MemberJoinedMessage {
  type: 'member:joined';
  room: string;
  member: MemberInfo;
}

export interface MemberLeftMessage {
  type: 'member:left';
  room: string;
  sessionId: string;
}

export interface RoomsUpdateMessage {
  type: 'rooms:update';
  rooms: RoomSummary[];
}

export interface QueryCreatedMessage {
  type: 'query:created';
  query: Query;
}

export interface QueryUpdatedMessage {
  type: 'query:updated';
  query: Query;
}

export interface DataRegeneratedMessage {
  type: 'data:regenerated';
  anchorDate: string;
  seed: string;
}

export interface PongMessage {
  type: 'pong';
  t: number;
  serverTime: string;
}

export interface ErrorMessage {
  type: 'error';
  code: 'bad_message' | 'not_identified' | 'room_full' | 'unknown_room' | 'not_allowed';
  message: string;
}

export type ServerMessage =
  | WelcomeMessage
  | RoomSnapshotMessage
  | MemberJoinedMessage
  | MemberLeftMessage
  | RoomsUpdateMessage
  | QueryCreatedMessage
  | QueryUpdatedMessage
  | DataRegeneratedMessage
  | PongMessage
  | ErrorMessage
  | RemotePoseMessage
  | RtcSignalRelayMessage
  | MemberUpdatedMessage
  | PinAddedMessage
  | PinRemovedMessage
  | ViewBroadcastMessage
  | ViewSetRelayMessage
  | HostSayRelayMessage
  | HostStopRelayMessage
  | AnnotationAddedMessage
  | AnnotationRemovedMessage
  | BeamRelayMessage
  | WbStrokeRelayMessage
  | WbNoteRelayMessage
  | WbEraseRelayMessage
  | WbClearRelayMessage
  | ModelPlacedMessage
  | ModelUpdatedMessage
  | ModelPinnedMessage
  | ModelUnpinnedMessage
  | ModelRemovedMessage
  | PresentStateMessage
  | PresentRecentreRelayMessage
  | ShareStateRelayMessage
  | ReactionRelayMessage;

export function isClientMessage(value: unknown): value is ClientMessage {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  return (
    t === 'hello' ||
    t === 'join' ||
    t === 'leave' ||
    t === 'ping' ||
    t === 'pose' ||
    t === 'rtc:signal' ||
    t === 'member:update' ||
    t === 'pin:add' ||
    t === 'pin:remove' ||
    t === 'broadcast:view' ||
    t === 'view:set' ||
    t === 'host:say' ||
    t === 'host:stop' ||
    t === 'annotation:add' ||
    t === 'annotation:remove' ||
    t === 'beam' ||
    t === 'wb:stroke' ||
    t === 'wb:note' ||
    t === 'wb:erase' ||
    t === 'wb:undo' ||
    t === 'wb:clear' ||
    t === 'model:place' ||
    t === 'model:update' ||
    t === 'model:grab' ||
    t === 'model:pin' ||
    t === 'model:unpin' ||
    t === 'model:remove' ||
    t === 'present:start' ||
    t === 'present:end' ||
    t === 'present:slide' ||
    t === 'present:spotlight' ||
    t === 'present:recentre' ||
    t === 'share:state' ||
    t === 'reaction'
  );
}
